import React, { useState } from "react";
import "./ExpandableYouTubeEmbed.css";

/**
 * Expandable YouTube embed component
 * Starts as a small preview and expands to full size on click
 * @param {string} videoId - YouTube video ID (from URL)
 * @param {string} title - Optional video title
 */
function ExpandableYouTubeEmbed({ videoId, title = "" }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract video ID from URL if full URL is provided
  const getVideoId = (idOrUrl) => {
    if (!idOrUrl) return null;
    
    // If it's already just an ID
    if (!idOrUrl.includes('youtube.com') && !idOrUrl.includes('youtu.be')) {
      return idOrUrl;
    }
    
    // Extract from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = idOrUrl.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const extractedVideoId = getVideoId(videoId);
  if (!extractedVideoId) return null;

  const embedUrl = `https://www.youtube.com/embed/${extractedVideoId}`;
  const thumbnailUrl = `https://img.youtube.com/vi/${extractedVideoId}/maxresdefault.jpg`;

  return (
    <div className={`expandable-youtube-embed ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {!isExpanded ? (
        <div 
          className="youtube-preview"
          onClick={() => setIsExpanded(true)}
        >
          <div className="youtube-thumbnail-wrapper">
            <img 
              src={thumbnailUrl}
              alt={title || "YouTube video thumbnail"}
              className="youtube-thumbnail"
            />
            <div className="youtube-play-button">
              <svg viewBox="0 0 68 48" width="68" height="48">
                <path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.63-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#f00"/>
                <path d="M 45,24 27,14 27,34" fill="#fff"/>
              </svg>
            </div>
            <div className="youtube-expand-hint">
              Click to expand
            </div>
          </div>
          {title && <p className="youtube-title">{title}</p>}
        </div>
      ) : (
        <div className="youtube-expanded-wrapper">
          <div className="youtube-expanded-header">
            <button 
              className="youtube-collapse-button"
              onClick={() => setIsExpanded(false)}
              aria-label="Collapse video"
            >
              ✕
            </button>
          </div>
          <div className="youtube-embed-container">
            <iframe
              src={`${embedUrl}?autoplay=1`}
              title={title || "YouTube video player"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="youtube-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpandableYouTubeEmbed;

